import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { VIDEO_COURSES_KEY } from '@/hooks/useVideoCourses'
import { needsContract, needsOnboarding } from '@/components/layout/onboardingAccess'
import { needsPlan } from '@/lib/planFeatures'

/**
 * MH13-19/MH14-04 — estado da primeira entrada, todo no servidor (`/first_access/*`).
 * So o aluno consulta: admin e instrutor nunca veem pop-up nem alerta.
 */

export const FIRST_ACCESS_VIDEOS_KEY = ['first-access', 'videos'] as const
export const ENTRANCE_TEST_KEY = ['first-access', 'entrance-test'] as const

function useIsStudent(): boolean {
  return useAuthStore((state) => state.user?.role === 'student')
}

export async function fetchFirstAccessVideos() {
  return (await api.firstAccess.videos()).data
}

/**
 * MH14-04: so depois de plano + onboarding + contrato. Configuracoes abre sem onboarding
 * (MH13-20) e o pop-up nao pode aparecer (nem ser encerrado) ali.
 */
function useIsPastGate(): boolean {
  return useAuthStore(
    (state) =>
      state.user?.role === 'student' &&
      !needsPlan(state.user) &&
      !needsOnboarding(state.user) &&
      !needsContract(state.user)
  )
}

/** MH14-04: a fila de videos obrigatorios do primeiro acesso (MH14-04b: toda entrada). */
export function useFirstAccessVideos() {
  const isPastGate = useIsPastGate()
  return useQuery({
    queryKey: FIRST_ACCESS_VIDEOS_KEY,
    queryFn: fetchFirstAccessVideos,
    enabled: isPastGate,
    staleTime: 1000 * 60 * 5,
  })
}

/** "Já assisti": grava a aula como assistida; a pagina de Video-aulas passa a mostrar. */
export function useMarkFirstAccessVideoWatched() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (lessonId: number) => (await api.firstAccess.markVideoWatched(lessonId)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIDEO_COURSES_KEY })
    },
  })
}

export function useEntranceTest() {
  const isStudent = useIsStudent()
  return useQuery({
    queryKey: ENTRANCE_TEST_KEY,
    queryFn: async () => (await api.firstAccess.entranceTest()).data,
    enabled: isStudent,
    staleTime: 1000 * 60 * 5,
  })
}
